/**
 * Service Layer Index
 *
 * Provides unified access to data services based on tenant configuration.
 * Routes to either LARX API or Arizona S3/CSV services depending on dataSource.
 */

import { getConfig, usesLarxDataSource } from '@/config'
import { degreeReqsApi } from './degreeReqsApi'
import * as arizonaService from './apiService'
import type { TenantConfig } from '@/config/types'
import type School from '@/models/school'
import type Course from '@/models/course'

/**
 * Get schools based on tenant configuration
 *
 * For LARX tenants: Returns schools from config
 * For Arizona: Returns hardcoded Arizona schools from apiService
 */
export function getSchools(): {
  startingSchools: School[]
  destinationSchools: School[]
} {
  const config = getConfig()

  if (usesLarxDataSource()) {
    // Convert config schools to School model format
    const startingSchools: School[] = config.institutions.startingSchools.map((s, index) => ({
      id: s.id ?? index + 1,
      name: s.name,
      slug: s.slug,
      disable: !s.enabled,
    }))

    const destinationSchools: School[] = [
      {
        id: config.institutions.destination.id ?? 1,
        name: config.institutions.destination.name,
        slug: config.institutions.destination.name.toLowerCase().replace(/\s+/g, '-'),
        disable: false,
      },
    ]

    return { startingSchools, destinationSchools }
  }

  // Arizona S3 path - use existing hardcoded schools
  return arizonaService.getSchools()
}

/**
 * Fetch courses for a school
 *
 * For LARX tenants: Uses local JSON catalog (e.g., /data/ccc_catalog.json)
 * For Arizona: Uses CSV from public/data/<slug>/CourseCatalog.csv
 */
export async function fetchSchoolCourses(school: School): Promise<Course[]> {
  if (usesLarxDataSource()) {
    return fetchSchoolCoursesFromJson(school)
  }
  // Arizona S3 path - use CSV
  return arizonaService.fetchSchoolCourses(school)
}

// Cache for JSON course catalogs
const jsonCourseCatalogCache = new Map<string, Course[]>()

/**
 * Fetch courses from JSON catalog for LARX tenants
 */
async function fetchSchoolCoursesFromJson(school: School): Promise<Course[]> {
  const slug = (school as any)?.slug
  if (!slug) throw new Error(`Missing school.slug for ${school?.name ?? 'unknown school'}`)

  const url = `/data/${slug}_catalog.json`

  // cache hit
  const cached = jsonCourseCatalogCache.get(url)
  if (cached) return cached

  const resp = await fetch(url, { cache: 'no-store' })
  if (!resp.ok) {
    throw new Error(`Failed to fetch course catalog JSON (${resp.status}): ${url}`)
  }

  const contentType = resp.headers.get('content-type') || ''
  const text = await resp.text()

  // If the server returned index.html (router fallback), fail loudly
  const looksHtml =
    contentType.includes('text/html') ||
    text.trim().startsWith('<!DOCTYPE html') ||
    text.trim().startsWith('<html')

  if (looksHtml) {
    throw new Error(`Expected JSON but got HTML from ${url}. Check public/data/${slug}_catalog.json path.`)
  }

  const data = JSON.parse(text)

  // Convert JSON catalog format to Course[]
  // Format: { catalog: { [id]: { name, credit_hours, id, num, prefix, ... } } }
  const catalog = data.catalog || data
  const courses: Course[] = Object.values(catalog).map((c: any) => ({
    id: c.id,
    name: c.name,
    code: `${c.prefix} ${c.num}`,
    full_name: `${c.prefix} ${c.num}: ${c.name}`,
    full_number: `${c.prefix} ${c.num}`,
    minimum_credits: c.credit_hours ?? c.minimum_credits ?? 0,
    maximum_credits: c.credit_hours ?? c.maximum_credits ?? 0,
    canonical_name: c.canonical_name || '',
    institution: c.institution || school.name,
    prefix: c.prefix,
    num: c.num,
  }))

  // Sort by prefix then number
  courses.sort((a, b) => {
    const prefixCmp = ((a as any).prefix || '').localeCompare((b as any).prefix || '')
    if (prefixCmp !== 0) return prefixCmp
    return ((a as any).num || '').localeCompare((b as any).num || '', undefined, { numeric: true })
  })

  jsonCourseCatalogCache.set(url, courses)
  return courses
}

/**
 * Generate completion plan
 *
 * For LARX tenants: Uses api.curricularanalytics.com
 * For Arizona: Uses api.azdegreeplans.com
 */
export async function generateCompletionPlan(data: {
  source_school: School
  dest_program: any
  taken_courses: Course[]
  completed_courses?: any[]  // LARX course equivalencies
  requirements?: any  // Program requirements
}): Promise<any> {
  const config = getConfig()

  if (usesLarxDataSource()) {
    // Use CurricularAnalytics API
    const caApiUrl = import.meta.env.VITE_CA_API_URL || 'https://api.curricularanalytics.com'
    const url = `${caApiUrl}/optimize/completion_plan`

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_CURRICULAR_ANALYTICS_API_KEY || '',
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      throw new Error(`Failed to generate completion plan: ${response.status}`)
    }

    return response.json()
  }

  // Arizona path
  return arizonaService.generateCompletionPlan(data)
}

/**
 * Generate two-to-four year plan
 *
 * For LARX tenants: Uses api.curricularanalytics.com
 * For Arizona: Uses api.azdegreeplans.com
 */
export async function generateTwoToFourPlan(data: {
  source_school: School
  source_program: any
  dest_school: School
  dest_program: any
}): Promise<any> {
  const config = getConfig()

  if (usesLarxDataSource()) {
    // Use CurricularAnalytics API
    const caApiUrl = import.meta.env.VITE_CA_API_URL || 'https://api.curricularanalytics.com'
    const url = `${caApiUrl}/optimize/two_to_four_plan`

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_CURRICULAR_ANALYTICS_API_KEY || '',
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      throw new Error(`Failed to generate two-to-four plan: ${response.status}`)
    }

    return response.json()
  }

  // Arizona path
  return arizonaService.generateTwoToFourPlan(data)
}

/**
 * Get LARX API service (only available for LARX tenants)
 *
 * @throws Error if called for non-LARX tenant
 */
export function getLarxService() {
  if (!usesLarxDataSource()) {
    throw new Error('LARX service is not available for this tenant')
  }
  return degreeReqsApi
}

// Re-export for direct access when needed
export { degreeReqsApi } from './degreeReqsApi'
export * as arizonaService from './apiService'
